import genericAvatar from "assets/images/generic_avatar.jpg";
import placeholderImg from "assets/images/placeholder_img.jpg";
import {
  HandshakeCareer,
  HiredTeacherPlacements,
  Mappable,
  StudentTeacher,
  MayTerm,
  PartnerSchool,
  CuratedTeacher,
  CuratedOrganization,
  Faculty,
} from "models";
import { Link } from "react-router-dom";
import React from "react";
// import { useAsync } from "react-async";
import { Col, Row } from "react-bootstrap";
import Img from "react-cool-img";
// import { getStudentTeacher, getMayTerm, getPartnerSchool } from "services";
import { URLPaths } from "utilities";
import {
  FaBriefcase,
  FaEnvelope,
  FaExpandAlt,
  FaGraduationCap,
  FaHandshake,
  FaLocationArrow,
  FaSchool,
  FaStar,
} from "react-icons/fa";
import "./PopoverItem.scss";
import { BsFillClockFill } from "react-icons/bs";
import { IoBusinessSharp, IoCalendarNumber } from "react-icons/io5";
import { MdDescription } from "react-icons/md";

/** Creating an interface for the PopoverItem function, for easier Popover Item generation */
interface PopoverItemProps<I extends Mappable | CuratedTeacher | CuratedOrganization | Faculty> {
  item: I;

  /** detail type of student teacher to distinguse between picture wall and JobOpeningsMap */
  detailType?: string;
}

/** Generating Popover Item based on the type of item which is either student-teacher or hired or career */
export function PopoverItem<I extends Mappable | CuratedTeacher | CuratedOrganization | Faculty>({
  item,
  detailType,
}: PopoverItemProps<I>): JSX.Element {
  switch (item.type) {
    case "may-term": {
      const mayTerm = item as unknown as MayTerm;
      return <MayTermPopover mayTerm={mayTerm} />;
    }
    case "partner-school": {
      const partnerSchool = item as unknown as PartnerSchool;
      return <PartnerSchoolPopover partnerSchool={partnerSchool} />;
    }
    case "student-teacher": {
      const studentTeacher = item as unknown as StudentTeacher;
      return <StudentTeacherPopover studentTeacher={studentTeacher} detailType={detailType} />;
    }
    case "hired": {
      const hiredPlacement = item as unknown as HiredTeacherPlacements;
      return <HiredPlacementPopover hiredPlacement={hiredPlacement} />;
    }
    case "career": {
      const job = item as unknown as HandshakeCareer;
      return <CareerPopoverItem career={job} />;
    }
    case "faculty": {
      const faculty = item as unknown as Faculty;
      return <FacultyPopover faculty={faculty} />;
    }
    default:
      return <span>#{item.id}</span>;
  }
}

/** Creating an interface for the StudentTeacherPopover function, for easier studentTeacher Popover Item generation */
interface MayTermPopoverProps {
  mayTerm: MayTerm;
}

/** Generating studentTeacherPopover Item with its properties */
function MayTermPopover({ mayTerm }: MayTermPopoverProps): JSX.Element {
  // const promiseFn = useCallback(() => getMayTerm(mayTerm.id), [mayTerm.id]);
  // const { data, isPending } = useAsync({ promiseFn });

  return (
    <Link className="popoverLink" to={`map/popover-may-term-details/${mayTerm.id}`}>
      <Row className="mayTerm-popover-item">
        <Col xs={3} className="d-flex align-items-center">
          <div className="img-circle-container">
            <Img
              src={mayTerm.photoUrl}
              placeholder={genericAvatar}
              alt={`${mayTerm.organization}`}
              width="100%"
            />
          </div>
        </Col>
        <Col className="d-flex align-items-center">
          <div>
            <h4 className="title">{`${mayTerm.location}`}</h4>
            <div className="secondary-info d-flex flex-column">
              {mayTerm.location && (
                <span>
                  <FaLocationArrow className="margin-right-9" color="grey" size={21} />
                  {mayTerm.location}
                </span>
              )}

              {mayTerm.date && (
                <span>
                  <IoCalendarNumber className="margin-less" color="grey" size={23} />
                  {mayTerm.date}
                </span>
              )}

              {mayTerm.description && (
                <span>
                  <MdDescription size={23} className="description-icon" />
                  {mayTerm.description.substring(0, 175)}...
                </span>
              )}
            </div>
          </div>
        </Col>
        <Col xs="auto">
          <FaExpandAlt size={25} />
        </Col>
      </Row>
    </Link>
  );
}
/** Creating an interface for the StudentTeacherPopover function, for easier studentTeacher Popover Item generation */
interface PartnerSchoolPopoverProps {
  partnerSchool: PartnerSchool;
}

/** Generating partnerSchoolPopover Item with its properties */
function PartnerSchoolPopover({ partnerSchool }: PartnerSchoolPopoverProps): JSX.Element {
  // const promiseFn = useCallback(() => getPartnerSchool(partnerSchool.id), [partnerSchool.id,]);
  // const { data, isPending } = useAsync({ promiseFn });

  return (
    <Link className="popoverLink" to={`map/popover-partner-school-details/${partnerSchool.id}`}>
      <Row className="partnerSchool-popover-item">
        <Col xs={3} className="d-flex align-items-center">
          <div className="img-circle-container">
            <Img
              src={partnerSchool.photoUrl}
              placeholder={genericAvatar}
              alt={`${partnerSchool.schoolName}`}
              width="100%"
              lazy
            />
          </div>
        </Col>
        <Col className="d-flex align-items-center">
          <div>
            <h4 className="title">{`${partnerSchool.schoolName}`}</h4>
            <div className="secondary-info d-flex flex-column">
              {/* Sue said we don't need the address, so no need to display it here
             <span>
              <i className="fas fa-location-arrow" />
              Address: {partnerSchool.location}
            </span> */}
              {partnerSchool.organization && ( // Only display this icon and info if the field is not empty
                <span>
                  <FaHandshake color="grey" size={24} className="margin-less" />
                  Partnership: {partnerSchool.organization}
                </span>
              )}
              {partnerSchool.partnerSince && (
                <span>
                  <BsFillClockFill size={20} className="margin-right" color="grey" />
                  Partner Since {partnerSchool.partnerSince}
                </span>
              )}
              {partnerSchool.description && (
                <span>
                  <MdDescription size={23} className="description-icon" color="grey" />
                  {partnerSchool.description.substring(0, 175)}...
                </span>
              )}
            </div>
          </div>
        </Col>
        <Col xs="auto">
          <FaExpandAlt size={25} />
        </Col>
      </Row>
    </Link>
  );
}

/** Creating an interface for the StudentTeacherPopover function, for easier studentTeacher Popover Item generation */
interface StudentTeacherPopoverProps {
  studentTeacher: StudentTeacher;
  detailType?: string;
}

/** Generating studentTeacherPopover Item with its properties */
function StudentTeacherPopover({
  studentTeacher,
  detailType,
}: StudentTeacherPopoverProps): JSX.Element {
  /* const promiseFn = useCallback(() => getStudentTeacher(studentTeacher.id), [
    studentTeacher.id,
  ]);
  const { data, isPending } = useAsync({ promiseFn });
  const str = studentTeacher.schoolAddress;
  const [first, ...rest] = str.split("," || "");
  const remainder = rest.join(","); */

  let detailPage = `map/popover-student-teacher-details/${studentTeacher.id}`;

  if (detailType === "picture-wall-student-teacher") {
    detailPage = `${URLPaths.pictureWall}/picture-wall-student-teacher-details/${studentTeacher.id}`;
  }

  function MajorFocus() {
    return (
      <span>
        <FaStar className="margin-less" size={23} color="grey" />
        Major: {studentTeacher.major.charAt(0).toUpperCase()}
        {studentTeacher.major.slice(1)}
      </span>
    );
  }

  return (
    <Link className="popoverLink" to={detailPage}>
      <Row className="studentTeacher-popover-item">
        <Col xs={3} className="d-flex align-items-center">
          <div className="img-circle-container">
            <Img
              src={studentTeacher.photoUrl}
              placeholder={genericAvatar}
              alt={`${studentTeacher.firstName} ${studentTeacher.lastName}`}
              width="100%"
              lazy
            />
          </div>
        </Col>
        <Col className="d-flex align-items-center">
          <div>
            <h4>{`${studentTeacher.firstName} ${studentTeacher.lastName}`} (Student-Teacher)</h4>
            <div className="secondary-info d-flex flex-column">
              {studentTeacher.gradYear && (
                <span>
                  <FaGraduationCap className="margin-less" size={23} color="grey" />
                  Class of {studentTeacher.gradYear}
                </span>
              )}
              {studentTeacher.major && (
                <span>
                  <MajorFocus />
                </span>
              )}
              {studentTeacher.schoolName && (
                <span>
                  <FaSchool size={22} className="margin-less" color="grey" />
                  Student Teacher at {studentTeacher.schoolName}
                </span>
              )}
              {studentTeacher.district && ( // this is the school district within which the student teacher is teaching
                <span>
                  <IoBusinessSharp size={22} color="grey" className="margin-less" />
                  District: {studentTeacher.district}
                </span>
              )}
            </div>
          </div>
        </Col>

        <Col xs="auto">
          <FaExpandAlt size={25} />
        </Col>
      </Row>
    </Link>
  );
}

/** Creating an interface for the FacultyPopover function, for easier studentTeacher Popover Item generation */
interface FacultyPopoverProps {
  faculty: Faculty;
}

/** Generating FacultyPopover Item with its properties */
function FacultyPopover({ faculty }: FacultyPopoverProps): JSX.Element {
  return (
    <Link
      className="popoverLink"
      to={`${URLPaths.pictureWall}/picture-wall-faculty-details/${faculty.id}`}
    >
      <Row className="studentTeacher-popover-item">
        <Col xs={3} className="d-flex align-items-center">
          <div className="img-circle-container">
            <Img
              src={faculty.photoUrl}
              placeholder={genericAvatar}
              alt={`${faculty.firstName} ${faculty.lastName}`}
              width="100%"
              lazy
            />
          </div>
        </Col>
        <Col className="d-flex align-items-center">
          <div>
            <h4>{`${faculty.firstName} ${faculty.lastName}`} (Faculty)</h4>
            <div className="secondary-info d-flex flex-column">
              {faculty.position && (
                <span>
                  <FaGraduationCap className="margin-less" size={23} color="grey" />
                  Hope College {faculty.position}
                </span>
              )}

              {faculty.coursesTaught && (
                <span>
                  <FaBriefcase className="margin-right" color="grey" />
                  Courses Taught: {faculty.coursesTaught}
                </span>
              )}

              {faculty.facultySince && (
                <span className="whitespace">
                  <BsFillClockFill className="margin-right" color="grey" />
                  Faculty Since {faculty.facultySince}
                </span>
              )}

              {faculty.email && (
                <span>
                  <FaEnvelope className="margin-right" color="grey" />
                  {faculty.email}
                </span>
              )}
            </div>
          </div>
        </Col>
        <Col xs="auto">
          <FaExpandAlt size={25} />
        </Col>
      </Row>
    </Link>
  );
}

/** Creating an interface for the HiredPlacement function, for easier HiredPlacement Popover Item generation */
interface HiredPlacementPopoverProps {
  hiredPlacement: HiredTeacherPlacements;
}

/** GeneratingHiredPlacementPopover Item with its properties */
function HiredPlacementPopover({ hiredPlacement }: HiredPlacementPopoverProps): JSX.Element {
  function MajorFocus() {
    return (
      <span>
        <FaStar className="margin-right" size={23} color="grey" />
        Major: {hiredPlacement.major.charAt(0).toUpperCase()}
        {hiredPlacement.major.slice(1)}
      </span>
    );
  }

  return (
    <Link className="popoverLink" to={`map/popover-hired-placement-details/${hiredPlacement.id}`}>
      <Row className="studentTeacher-popover-item">
        <Col xs={3} className="d-flex align-items-center">
          <div className="img-circle-container">
            <Img
              src={hiredPlacement.photoUrl}
              placeholder={genericAvatar}
              alt={`${hiredPlacement.firstName} ${hiredPlacement.lastName}`}
              width="100%"
              lazy
            />
          </div>
        </Col>
        <Col className="d-flex align-items-center">
          <div>
            <h4>{`${hiredPlacement.firstName} ${hiredPlacement.lastName}`} (Alumni)</h4>
            <div className="secondary-info d-flex flex-column">
              {hiredPlacement.gradYear ? (
                <span>
                  <FaGraduationCap className="margin-right" size={23} color="grey" />
                  Hope College Class of {hiredPlacement.gradYear}
                </span>
              ) : (
                <span>
                  <FaGraduationCap className="margin-right" size={23} color="grey" />
                  Hope College Alumni
                </span>
              )}

              {hiredPlacement.major && (
                <span>
                  <MajorFocus />
                </span>
              )}

              {hiredPlacement.currentPosition ? (
                hiredPlacement.schoolName ? (
                  <span>
                    <FaBriefcase size={22} className="margin-right" color="grey" />
                    {hiredPlacement.currentPosition} at {hiredPlacement.schoolName}
                  </span>
                ) : (
                  <span>
                    <FaBriefcase size={22} className="margin-right" color="grey" />
                    {hiredPlacement.currentPosition}
                  </span>
                )
              ) : (
                hiredPlacement.schoolName && (
                  <span>
                    <FaBriefcase className="margin-right" color="grey" />
                    {hiredPlacement.schoolName}
                  </span>
                )
              )}
            </div>
          </div>
        </Col>
        <Col xs="auto">
          <FaExpandAlt size={25} />
        </Col>
      </Row>
    </Link>
  );
}

/** Creating an interface for the CareerPopoverItem function, for easier Career Popover Item generation */
interface CareerPopoverItemProps {
  career: HandshakeCareer;
}

/** Generating Career Item with its properties */
function CareerPopoverItem({ career }: CareerPopoverItemProps): JSX.Element {
  return (
    <Row className="job-popover-item">
      <Col xs={3} className="d-flex align-items-center">
        <div>
          <Img
            src={career.employer_logo_url}
            placeholder={placeholderImg}
            width="100%"
            loading="lazy"
          />
        </div>
      </Col>
      <Col className="d-flex align-items-center">
        <div>
          <h4>{career.job_name}</h4>
          <div className="secondary-info d-flex flex-column">
            {career.employer_name && (
              <span>
                <FaBriefcase className="margin-right" color="grey" />
                {career.employer_name}
              </span>
            )}
            {career.employment_type_name && (
              <span>
                <BsFillClockFill className="margin-right" color="grey" />
                {career.employment_type_name}
              </span>
            )}
            {career.job_city && (
              <span>
                <FaLocationArrow className="margin-right" color="grey" />
                {career.job_city}
              </span>
            )}
          </div>
        </div>
      </Col>
    </Row>
  );
}
